import React, { useEffect, useState } from 'react';
import { getProducts, createSale } from '../constants/api';
import { COLORS } from '../constants/theme';

export default function RecordSalePage() {
  const isMobile = window.innerWidth < 768;
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getProducts()
      .then(res => setProducts(res.data))
      .catch(err => console.log(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) && p.quantity > 0);
  const total = selected ? selected.sellingPrice * quantity : 0;
  const profit = selected ? (selected.sellingPrice - selected.costPrice) * quantity : 0;

  const handleRecord = async () => {
    if (!selected) return setError('Please select a product.');
    if (quantity <= 0) return setError('Quantity must be greater than 0.');
    if (quantity > selected.quantity) return setError(`Only ${selected.quantity} units available.`);
    setSaving(true);
    setError('');
    try {
      await createSale({ productId: selected.id, quantitySold: quantity });
      setSuccess({ product: selected.name, quantity, total, profit });
      setSelected(null);
      setQuantity(1);
      setSearch('');
      const res = await getProducts();
      setProducts(res.data);
    } catch (err) {
      setError(err?.response?.data ?? 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: COLORS.accent }}>Loading...</div>
  );

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', maxWidth: 800, margin: '0 auto' }}>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: COLORS.ink }}>Record Sale</div>
        <div style={{ fontSize: 13, color: COLORS.inkLight, marginTop: 4 }}>Select a product and quantity</div>
      </div>

      {/* Success banner */}
      {success && (
        <div style={{ background: COLORS.accentLight, border: `1px solid #B6DFC7`, borderRadius: 14, padding: 16, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.accent }}>Sale recorded! ✅</div>
            <div style={{ fontSize: 13, color: COLORS.inkMid, marginTop: 3 }}>{success.quantity}x {success.product}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: COLORS.accent }}>+₦{success.profit.toLocaleString()}</div>
            <div style={{ fontSize: 11, color: COLORS.inkLight }}>profit</div>
          </div>
          <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.inkLight, marginLeft: 12 }}>✕</button>
        </div>
      )}

      {/* Search */}
      <input
        type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, color: COLORS.ink, background: 'white', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 12 }}
        onFocus={e => e.target.style.borderColor = COLORS.accent}
        onBlur={e => e.target.style.borderColor = COLORS.border}
      />

      {/* Product list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: COLORS.inkLight, fontSize: 13 }}>No products found</div>
        ) : (
          filtered.map(product => {
            const isSelected = selected?.id === product.id;
            return (
              <div
                key={product.id}
                onClick={() => { setSelected(product); setQuantity(1); setError(''); }}
                style={{ padding: '14px 16px', borderRadius: 12, border: `2px solid ${isSelected ? COLORS.accent : COLORS.border}`, background: isSelected ? COLORS.accentLight : 'white', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isSelected ? COLORS.accent : COLORS.ink }}>{product.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 2 }}>₦{product.sellingPrice.toLocaleString()} · {product.quantity} in stock</div>
                </div>
                {isSelected && <div style={{ width: 22, height: 22, borderRadius: '50%', background: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 800 }}>✓</div>}
              </div>
            );
          })
        )}
      </div>

      {/* Sale details — shows when product selected */}
      {selected && (
        <div style={{ background: 'white', borderRadius: 16, border: `1px solid ${COLORS.border}`, padding: 20 }}>
          <div style={{ background: COLORS.accentLight, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.accent }}>{selected.name}</div>
            <div style={{ fontSize: 12, color: COLORS.inkMid, marginTop: 3 }}>₦{selected.sellingPrice.toLocaleString()} per unit · {selected.quantity} available</div>
          </div>

          {/* Quantity */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, marginBottom: 8 }}>Quantity</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={{ width: 44, height: 44, borderRadius: 10, border: `1.5px solid ${COLORS.border}`, background: 'white', fontSize: 22, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.inkMid }}>−</button>
              <input type="number" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                style={{ width: 80, padding: '10px', borderRadius: 10, border: `1.5px solid ${COLORS.accent}`, fontSize: 18, fontWeight: 800, textAlign: 'center', color: COLORS.ink, background: COLORS.accentLight, outline: 'none', fontFamily: 'inherit' }} />
              <button onClick={() => setQuantity(q => Math.min(selected.quantity, q + 1))} style={{ width: 44, height: 44, borderRadius: 10, border: `1.5px solid ${COLORS.border}`, background: 'white', fontSize: 22, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.inkMid }}>+</button>
            </div>
          </div>

          {/* Summary */}
          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: COLORS.inkLight }}>Total</span>
              <span style={{ fontSize: 20, fontWeight: 900, color: COLORS.ink }}>₦{total.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', background: COLORS.accentLight, borderRadius: 10, padding: '10px 14px' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent }}>Profit</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: COLORS.accent }}>+₦{profit.toLocaleString()}</span>
            </div>
          </div>

          {error && <div style={{ background: '#FEE2E2', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600, marginBottom: 12 }}>{error}</div>}

          <button onClick={handleRecord} disabled={saving} style={{ width: '100%', background: COLORS.accent, color: 'white', border: 'none', borderRadius: 12, padding: '16px', fontSize: 15, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit' }}>
            {saving ? 'Recording...' : '✅ Record Sale'}
          </button>
        </div>
      )}
    </div>
  );
}